import merge from 'lodash/merge';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { fetchCurrentUser, currentUserShowSuccess } from '../../ducks/user.duck';
import { createNewListing, fetchConsent } from '../../util/api';
import { saveConsentFor, ConsentSaveFailedError } from '../../util/actionConsent';

// ================ Action types ================ //

export const SAVE_CONTACT_DETAILS_REQUEST = 'app/TwilioConsentPage/SAVE_CONTACT_DETAILS_REQUEST';
export const SAVE_CONTACT_DETAILS_SUCCESS = 'app/TwilioConsentPage/SAVE_CONTACT_DETAILS_SUCCESS';
export const SAVE_EMAIL_ERROR = 'app/TwilioConsentPage/SAVE_EMAIL_ERROR';
export const SAVE_PHONE_NUMBER_ERROR = 'app/TwilioConsentPage/SAVE_PHONE_NUMBER_ERROR';

export const SAVE_CONTACT_DETAILS_CLEAR = 'app/TwilioConsentPage/SAVE_CONTACT_DETAILS_CLEAR';

export const RESET_PASSWORD_REQUEST = 'app/TwilioConsentPage/RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'app/TwilioConsentPage/RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_ERROR = 'app/TwilioConsentPage/RESET_PASSWORD_ERROR';

export const PROFILE_LISTING_REQUEST = 'app/TwilioConsentPage/PROFILE_LISTING_REQUEST';
export const PROFILE_LISTING_SUCCESS = 'app/TwilioConsentPage/PROFILE_LISTING_SUCCESS';
export const PROFILE_LISTING_ERROR = 'app/TwilioConsentPage/PROFILE_LISTING_ERROR';

export const UPDATE_TWILIO_SUB_REQUEST = 'app/TwilioConsentPage/UPDATE_TWILIO_SUB_REQUEST';
export const UPDATE_TWILIO_SUB_SUCCESS = 'app/TwilioConsentPage/UPDATE_TWILIO_SUB_SUCCESS';
export const UPDATE_TWILIO_SUB_ERROR = 'app/TwilioConsentPage/UPDATE_TWILIO_SUB_ERROR';

export const SET_UPDATING = 'app/TwilioConsentPage/SET_UPDATING';

export const SET_SMS_CONSENT_SAVED = 'app/TwilioConsentPage/SET_SMS_CONSENT_SAVED';

// ================ Reducer ================ //

const initialState = {
  saveEmailError: null,
  savePhoneNumberError: null,
  saveContactDetailsInProgress: false,
  contactDetailsChanged: false,
  resetPasswordInProgress: false,
  resetPasswordError: null,
  profileListing: null,
  profileListingError: null,
  twilioSubUpdated: false,
  updating: false,
  // Whether an SMS consent is saved for this user: null until known, or when it cannot be checked.
  smsConsentSaved: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_CONTACT_DETAILS_REQUEST:
      return {
        ...state,
        saveContactDetailsInProgress: true,
        saveEmailError: null,
        savePhoneNumberError: null,
        contactDetailsChanged: false,
      };
    case SAVE_CONTACT_DETAILS_SUCCESS:
      return { ...state, saveContactDetailsInProgress: false, contactDetailsChanged: true };
    case SAVE_EMAIL_ERROR:
      return { ...state, saveContactDetailsInProgress: false, saveEmailError: payload };
    case SAVE_PHONE_NUMBER_ERROR:
      return { ...state, saveContactDetailsInProgress: false, savePhoneNumberError: payload };

    case SAVE_CONTACT_DETAILS_CLEAR:
      return {
        ...state,
        saveContactDetailsInProgress: false,
        saveEmailError: null,
        savePhoneNumberError: null,
        contactDetailsChanged: false,
      };

    case RESET_PASSWORD_REQUEST:
      return { ...state, resetPasswordInProgress: true, resetPasswordError: null };
    case RESET_PASSWORD_SUCCESS:
      return { ...state, resetPasswordInProgress: false };
    case RESET_PASSWORD_ERROR:
      return { ...state, resetPasswordInProgress: false, resetPasswordError: payload };

    case PROFILE_LISTING_REQUEST:
      return { ...state, profileListing: null, profileListingError: null };
    case PROFILE_LISTING_SUCCESS:
      return { ...state, profileListing: payload };
    case PROFILE_LISTING_ERROR:
      return { ...state, profileListingError: payload };

    case UPDATE_TWILIO_SUB_REQUEST:
      return { ...state, twilioSubUpdated: false };
    case UPDATE_TWILIO_SUB_SUCCESS:
      return { ...state, twilioSubUpdated: true };
    case UPDATE_TWILIO_SUB_ERROR:
      return { ...state, twilioSubUpdated: false };
      
    case SET_UPDATING:
      return { ...state, updating: payload };

    case SET_SMS_CONSENT_SAVED:
      return { ...state, smsConsentSaved: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const saveContactDetailsRequest = () => ({ type: SAVE_CONTACT_DETAILS_REQUEST });
export const saveContactDetailsSuccess = () => ({ type: SAVE_CONTACT_DETAILS_SUCCESS });
export const saveEmailError = error => ({
  type: SAVE_EMAIL_ERROR,
  payload: error,
  error: true,
});
export const savePhoneNumberError = error => ({
  type: SAVE_PHONE_NUMBER_ERROR,
  payload: error,
  error: true,
});

export const saveContactDetailsClear = () => ({ type: SAVE_CONTACT_DETAILS_CLEAR });

export const resetPasswordRequest = () => ({ type: RESET_PASSWORD_REQUEST });

export const resetPasswordSuccess = () => ({ type: RESET_PASSWORD_SUCCESS });

export const resetPasswordError = e => ({
  type: RESET_PASSWORD_ERROR,
  error: true,
  payload: e,
});

export const profileListingRequest = () => ({ type: PROFILE_LISTING_REQUEST });
export const profileListingSuccess = payload => ({
  type: PROFILE_LISTING_SUCCESS,
  payload,
});
export const profileListingError = e => ({
  type: PROFILE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const updateTwilioSubRequest = () => ({ type: UPDATE_TWILIO_SUB_REQUEST });
export const updateTwilioSubSuccess = () => ({ type: UPDATE_TWILIO_SUB_SUCCESS });
export const updateTwilioSubError = e => ({
  type: UPDATE_TWILIO_SUB_ERROR,
  error: true,
  payload: e,
});

export const setUpdating = payload => ({ type: SET_UPDATING, payload });

export const setSmsConsentSaved = payload => ({ type: SET_SMS_CONSENT_SAVED, payload });

// ================ Thunks ================ //

/**
 * Make a phone number update request to the API and return the current user.
 */
const requestSavePhoneNumber = params => (dispatch, getState, sdk) => {
  const phoneNumber = params.phoneNumber;

  return sdk.currentUser
    .updateProfile(
      { protectedData: { phoneNumber } },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      const currentUser = entities[0];
      return currentUser;
    })
    .catch(e => {
      dispatch(savePhoneNumberError(storableError(e)));
      // pass the same error so that the SAVE_CONTACT_DETAILS_SUCCESS
      // action will not be fired
      throw e;
    });
};

/**
 * Make a email update request to the API and return the current user.
 */
const requestSaveEmail = params => (dispatch, getState, sdk) => {
  const { email, currentPassword } = params;

  return sdk.currentUser
    .changeEmail(
      { email, currentPassword },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.changeEmail response');
      }

      const currentUser = entities[0];
      return currentUser;
    })
    .catch(e => {
      dispatch(saveEmailError(storableError(e)));
      // pass the same error so that the SAVE_CONTACT_DETAILS_SUCCESS
      // action will not be fired
      throw e;
    });
};

/**
 * Save email and update the current user.
 */
const saveEmail = params => (dispatch, getState, sdk) => {
  return (
    dispatch(requestSaveEmail(params))
      .then(user => {
        dispatch(currentUserShowSuccess(user));
        dispatch(saveContactDetailsSuccess());
      })
      // error action dispatched in requestSaveEmail
      .catch(e => null)
  );
};

/**
 * Save phone number and update the current user.
 */
const savePhoneNumber = params => (dispatch, getState, sdk) => {
  return (
    dispatch(requestSavePhoneNumber(params))
      .then(user => {
        dispatch(currentUserShowSuccess(user));
        dispatch(saveContactDetailsSuccess());
      })
      // error action dispatched in requestSavePhoneNumber
      .catch(e => null)
  );
};

/**
 * Save email and phone number and update the current user.
 */
const saveEmailAndPhoneNumber = params => (dispatch, getState, sdk) => {
  const { email, phoneNumber, currentPassword } = params;

  // order of promises: 1. email, 2. phone number
  const promises = [
    dispatch(requestSaveEmail({ email, currentPassword })),
    dispatch(requestSavePhoneNumber({ phoneNumber })),
  ];

  return Promise.all(promises)
    .then(values => {
      // Array of two user objects is resolved
      // the first one is from the email update
      // the second one is from the phone number update

      const saveEmailUser = values[0];
      const savePhoneNumberUser = values[1];

      // merge the protected data from the user object returned
      // by the phone update operation
      const protectedData = savePhoneNumberUser.attributes.profile.protectedData;
      const phoneNumberMergeSource = { attributes: { profile: { protectedData } } };

      const currentUser = merge(saveEmailUser, phoneNumberMergeSource);
      dispatch(currentUserShowSuccess(currentUser));
      dispatch(saveContactDetailsSuccess());
    })
    .catch(e => null);
};

/**
 * Update contact details, actions depend on which data has changed
 */
export const saveContactDetails = params => (dispatch, getState, sdk) => {
  dispatch(saveContactDetailsRequest());

  const { email, currentEmail, phoneNumber, currentPhoneNumber, currentPassword } = params;
  const emailChanged = email !== currentEmail;
  const phoneNumberChanged = phoneNumber !== currentPhoneNumber;

  if (emailChanged && phoneNumberChanged) {
    return dispatch(saveEmailAndPhoneNumber({ email, currentPassword, phoneNumber }));
  } else if (emailChanged) {
    return dispatch(saveEmail({ email, currentPassword }));
  } else if (phoneNumberChanged) {
    return dispatch(savePhoneNumber({ phoneNumber }));
  }
};

export const resetPassword = email => (dispatch, getState, sdk) => {
  dispatch(resetPasswordRequest());
  return sdk.passwordReset
    .request({ email })
    .then(() => dispatch(resetPasswordSuccess()))
    .catch(e => dispatch(resetPasswordError(storableError(e))));
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  // Since verify email happens in separate tab, current user's data might be updated

  return sdk.currentUser.show()
    .then(async response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }

      const currentUser = entities[0];

      let currentUserProfileListingId = currentUser?.attributes?.profile?.publicData?.profileListingId;
      if (!currentUserProfileListingId) {
        throw new Error('User is not associated with a profile listing');
      }

      try {
        dispatch(profileListingRequest());

        let profileListing = await sdk.listings.show({ id: currentUserProfileListingId });
        profileListing = profileListing?.data?.data;

        dispatch(profileListingSuccess(profileListing));
      } catch (error) {
        dispatch(profileListingError(storableError(error)));
      }

      // Asked without a listing id, since older consents may not carry one. If the check fails the
      // answer stays unknown and the rest of the page loads as usual.
      try {
        const consent = await fetchConsent({ consentType: 'TwilioSMS' });
        dispatch(setSmsConsentSaved(!!consent?.hasConsent));
      } catch (error) {
        dispatch(setSmsConsentSaved(null));
      }

      dispatch(currentUserShowSuccess(currentUser));
    })
    .catch(e => dispatch(profileListingError(storableError(e))));
};

// Turning SMS on saves the SMS consent first and sends its id with the opt-in, so the opt-in is
// never recorded without it. Turning SMS off needs no consent.
export const updateTwilioSub = params => async (dispatch, getState, sdk) => {
  dispatch(updateTwilioSubRequest());
  dispatch(setUpdating(true));

  let consentIdMaybe = {};
  if (params.twilioSubscribed) {
    try {
      const consentId = await saveConsentFor('TwilioSMS', {
        candidateListingId: params.profileListingId,
      });
      consentIdMaybe = { consentId };
    } catch (e) {
      dispatch(updateTwilioSubError(storableError(e)));
      dispatch(setUpdating(false));
      return { ok: false, reason: 'consent_save_failed' };
    }
  }

  try {
    await createNewListing({ ...params, ...consentIdMaybe, updateTwilioConsent: true });
  } catch (e) {
    dispatch(updateTwilioSubError(storableError(e)));
    dispatch(setUpdating(false));
    return { ok: false, reason: 'update_failed', error: e };
  }

  dispatch(updateTwilioSubSuccess());
  dispatch(loadData());
  dispatch(setUpdating(false));
  return { ok: true };
};

// Saves the SMS consent for someone already subscribed whose consent is missing, so their texts
// resume. A consent that cannot be saved answers { ok: false } and leaves the page as it was.
export const confirmSmsConsent = profileListingId => async (dispatch, getState, sdk) => {
  try {
    await saveConsentFor('TwilioSMS', { candidateListingId: profileListingId });
  } catch (e) {
    if (e instanceof ConsentSaveFailedError) {
      return { ok: false };
    }
    throw e;
  }
  dispatch(setSmsConsentSaved(true));
  return { ok: true };
};
